// Phone-number helpers — keep formatting consistent everywhere phones are
// entered, displayed, or used in tel:/sms: URLs.
//
// Stored format: "(NNN) NNN-NNNN" for US 10-digit. Anything that can't be
// reduced to 10 (or 11 with a leading 1) is stored as the user typed it,
// so international numbers and partials still round-trip.
//
// digitsOnly() / telHref() / smsHref() always return a clean numeric string
// safe to drop into an `href`.

/** Strip everything except digits (and an optional leading "+"). */
export function digitsOnly(input) {
  if (!input) return "";
  const s = String(input).trim();
  // Preserve a leading + for international numbers.
  if (s.startsWith("+")) return "+" + s.slice(1).replace(/\D/g, "");
  return s.replace(/\D/g, "");
}

/**
 * Pretty-print as "(NNN) NNN-NNNN" for US 10-digit, or "+1 (NNN) NNN-NNNN"
 * for 11-digit starting with 1. Otherwise return the input unchanged so we
 * don't mangle international numbers or partials.
 */
export function formatPhone(input) {
  if (!input) return "";
  const raw = String(input);
  const d = digitsOnly(raw);

  // International (starts with + and isn't a plain US +1 number)
  if (raw.trim().startsWith("+")) {
    if (d.length === 12 && d.startsWith("+1")) {
      const ten = d.slice(2);
      return `+1 (${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
    }
    return raw.trim();
  }

  if (d.length === 10) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  if (d.length === 11 && d.startsWith("1")) {
    return `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`;
  }
  // Mid-typing — show progressive formatting so the field never lurches.
  if (d.length >= 7) {
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
  }
  if (d.length >= 4) {
    return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  }
  if (d.length > 0) {
    return `(${d}`;
  }
  return raw;
}

/** Build a tel: href. Returns null if no usable digits. */
export function telHref(input) {
  const d = digitsOnly(input);
  if (!d) return null;
  return `tel:${d}`;
}

/** Build an sms: href with optional pre-filled body. iOS uses ?body=. */
export function smsHref(input, body) {
  const d = digitsOnly(input);
  if (!d) return null;
  return body
    ? `sms:${d}?body=${encodeURIComponent(body)}`
    : `sms:${d}`;
}
